import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SaveStatusBadge from "./SaveStatusBadge";
import { ClassificationLabels } from "../../models/classification.map";
import { classificationLabel, classificationStyle } from "../../models/classificationStyle";
import "./ProductionTable.css";

// Éléments communs aux tableaux de production Robot et Étiquetage.

export const STATUTS_HORS_PRODUCTION = new Set(["EN_LIVRAISON", "LIVRE"]);
export const PRODUCTION_PAGE_SIZE = 50;
export const CLIENT_CLASSIFICATION_FILTERS = [
    { value: "", label: "Tout" },
    { value: "URGENCES", label: "Urgences" },
    { value: "LISTE_HP", label: "Liste HP" },
];
const HIDDEN_DETAILED_CLASSIFICATIONS = new Set([
    "UNKNOWN",
    "PRIMO_MISE_EN_ATTENTE",
    "PAS_DE_MODIF_NOUVELLE_ORDO",
    "RAS_NOUVELLE_ORDO",
]);
export const DETAILED_CLASSIFICATION_FILTERS = Object.entries(ClassificationLabels)
    .filter(([key]) => !HIDDEN_DETAILED_CLASSIFICATIONS.has(key));

export function matchesClassificationFilters(classification, groupFilter, detailFilter) {
    if (groupFilter === "LISTE_HP" && classification !== "LISTE_HP") return false;
    if (groupFilter === "URGENCES" && classification === "LISTE_HP") return false;
    if (detailFilter && classification !== detailFilter) return false;
    return true;
}

export function formatDate(str) {
    if (!str) return "";
    const d = new Date(str);
    const yy = String(d.getFullYear()).slice(2);
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${yy} · ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
}

export function formatDateTime(str) {
    if (!str) return null;
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return str;
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")} · ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
}

function formatPrintDate(str) {
    if (!str) return null;
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return str;
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
}

function escapeHtml(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}

function formatPatientName(nom, prenom) {
    return [nom ? String(nom).toLocaleUpperCase("fr") : "", prenom || ""]
        .filter(Boolean)
        .join(" ")
        .trim();
}

function Cip13Badge({ code }) {
    const [copied, setCopied] = useState(false);
    const [hovered, setHovered] = useState(false);
    if (!code) return null;

    const showCopyIcon = hovered || copied;

    const copy = async (event) => {
        event.stopPropagation();
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch {
            setCopied(false);
        }
    };

    return (
        <button
            type="button"
            onClick={copy}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setHovered(true)}
            onBlur={() => setHovered(false)}
            aria-label={copied ? "Code CIP13 copié" : "Cliquer pour copier"}
            title={copied ? `Code CIP13 copié : ${code}` : `CIP13 : ${code}`}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                border: "1px solid #99f6e4",
                background: copied ? "#ccfbf1" : "#f0fdfa",
                color: "#0f766e",
                borderRadius: "6px",
                padding: "2px 6px",
                fontSize: "10.5px",
                fontWeight: 800,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                maxWidth: "100%",
                overflow: "hidden",
            }}
        >
            <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                width="13"
                height="13"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0, opacity: showCopyIcon ? 1 : 0 }}
            >
                <rect x="8" y="8" width="12" height="12" rx="2" />
                <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
            </svg>
            <span title={`CIP13 : ${code}`} style={{ overflow: "hidden", textOverflow: "ellipsis" }}>CIP13: {code}</span>
        </button>
    );
}

function BdpmDisponibiliteBadge({ statut }) {
    if (!statut) return null;
    return (
        <span
            title={`Statut BDPM : ${statut}`}
            style={{
                border: "1px solid #fecdd3",
                background: "#fff1f2",
                color: "#be123c",
                borderRadius: "6px",
                padding: "2px 6px",
                fontSize: "10.5px",
                fontWeight: 800,
                whiteSpace: "nowrap",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "inline-block",
            }}
        >
            BDPM {statut}
        </span>
    );
}

function ProductBdpmBadges({ row }) {
    if (!row.cip13 && !row.bdpmDisponibiliteStatuts) return null;
    return (
        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", alignItems: "center", minWidth: 0, maxWidth: "100%" }}>
            <Cip13Badge code={row.cip13} />
            <BdpmDisponibiliteBadge statut={row.bdpmDisponibiliteStatuts} />
        </div>
    );
}

// La largeur de la colonne produit reste en ligne : elle dimensionne le tableau.
export const productCellStyle = {
    whiteSpace: "normal",
    width: "240px",
    minWidth: "160px",
    maxWidth: "240px",
};

function isDisplayableDosage(dosage) {
    const normalized = String(dosage || "").trim().toLowerCase();
    return normalized && normalized !== "achat" && normalized !== "location";
}

const NAME_BUBBLE_GAP = 6;
const NAME_BUBBLE_MAX_HEIGHT = 80;

/** Nom de produit limité à deux lignes ; s'il est tronqué, une bulle affiche le nom complet au survol. */
function ProductName({ text }) {
    const ref = useRef(null);
    const [bubble, setBubble] = useState(null);

    useEffect(() => {
        if (!bubble) return undefined;
        const hide = () => setBubble(null);
        window.addEventListener("scroll", hide, true);
        return () => window.removeEventListener("scroll", hide, true);
    }, [bubble]);

    const show = () => {
        const el = ref.current;
        if (!el || (el.scrollHeight <= el.clientHeight && el.scrollWidth <= el.clientWidth)) return;
        const rect = el.getBoundingClientRect();
        const below = rect.bottom + NAME_BUBBLE_GAP + NAME_BUBBLE_MAX_HEIGHT <= window.innerHeight;
        setBubble({
            left: rect.left,
            top: below ? rect.bottom + NAME_BUBBLE_GAP : rect.top - NAME_BUBBLE_GAP,
            placement: below ? "below" : "above",
        });
    };

    return (
        <>
            <span
                ref={ref}
                className="rbt-product__name"
                onMouseEnter={show}
                onMouseLeave={() => setBubble(null)}
            >
                {text}
            </span>
            {bubble && createPortal(
                <span
                    role="tooltip"
                    className={`rbt-name-bubble rbt-name-bubble--${bubble.placement}`}
                    style={{ left: bubble.left, top: bubble.top }}
                >
                    {text}
                </span>,
                document.body
            )}
        </>
    );
}

export function ProductSummary({ row, printSelected = false }) {
    const showDosage = isDisplayableDosage(row.dosage) && !row.nom?.includes(row.dosage);

    return (
        <div className={`rbt-product${printSelected ? " is-print-selected" : ""}`}>
            <span className="rbt-product__dot" aria-hidden="true" />
            <div className="rbt-product__body">
                <ProductName text={row.nom || "—"} />
                {showDosage && <span className="rbt-product__dosage">{row.dosage}</span>}
                <ProductBdpmBadges row={row} />
            </div>
            <MedClassifBadge value={row.classificationMedicament} />
        </div>
    );
}

export function productTone({ enRupture, done, planned }) {
    if (enRupture) return "rupture";
    if (done) return "done";
    if (planned) return "planned";
    return "default";
}

function etiquetteBody(row, pharmacy) {
    const patientName = formatPatientName(row.patientNom, row.patientPrenom);
    const produit = [row.nom, row.dosage && !row.nom?.includes(row.dosage) ? row.dosage : null]
        .filter(Boolean)
        .join(" ");
    const classification = row.classification && row.classification !== "UNKNOWN"
        ? classificationLabel(row.classification)
        : "—";

    return `
    <main class="label">
        <div class="header"></div>
        <div class="body">
            <div class="line line1">
                <span class="date">${escapeHtml(formatPrintDate(new Date().toISOString()) || "—")}</span>
                <span class="pharmacy">${escapeHtml(pharmacy?.name || "Pharmacie non renseignée")}</span>
            </div>
            <div class="line line2">${escapeHtml(row.ehpadCode || "Code EHPAD non renseigné")}</div>
            <div class="line line3">${escapeHtml(patientName || "Patient non renseigné")}</div>
            <div class="line line4">${escapeHtml(produit || "Produit non renseigné")}</div>
            <div class="line line5">${escapeHtml(classification)}</div>
        </div>
    </main>`;
}

function generateEtiquettePrintHTML(rows, pharmacy, title) {
    return `<!doctype html>
<html lang="fr">
<head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
        @page {
            size: 50.8mm 32mm;
            margin: 0;
        }
        * { box-sizing: border-box; }
        html, body {
            margin: 0;
            padding: 0;
            background: white;
            color: #111827;
            font-family: Arial, Helvetica, sans-serif;
        }
        body {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }
        .label {
            width: 50.8mm;
            height: 32mm;
            padding: 1.5mm 1.3mm;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            background: #d6d3c2;
            break-after: page;
            page-break-after: always;
        }
        .label:last-child {
            break-after: auto;
            page-break-after: auto;
        }
        .header {
            height: 4.5mm;
            background: #ffffff;
            border-radius: 1.5mm 1.5mm 0 0;
        }
        .body {
            flex: 1;
            background: #95ef23;
            border-radius: 0 0 1.5mm 1.5mm;
            padding: 2mm 1.6mm 1.6mm;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            gap: 0.45mm;
            line-height: 1;
            overflow: hidden;
        }
        .line {
            width: 100%;
            min-width: 0;
            color: #0f172a;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .line1 {
            display: flex;
            align-items: baseline;
            gap: 1.4mm;
            font-size: 9pt;
            font-weight: 500;
        }
        .date {
            flex: 0 0 auto;
            font-weight: 600;
        }
        .pharmacy {
            flex: 1 1 auto;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            font-weight: 500;
        }
        .line2 {
            font-size: 10.4pt;
            font-weight: 900;
            text-transform: uppercase;
        }
        .line3,
        .line4,
        .line5 {
            font-weight: 900;
        }
        .line3 {
            font-size: 11.8pt;
        }
        .line2,
        .line4,
        .line5 {
            text-transform: uppercase;
        }
        .line4 {
            font-size: 10.5pt;
        }
        .line5 {
            font-size: 9.4pt;
        }
        @media screen {
            body { background: #f3f4f6; padding: 8mm; gap: 8mm; }
            .label { border: 1px dashed #9ca3af; }
        }
        @media print {
            body { padding: 0; gap: 0; }
        }
    </style>
</head>
<body>
${rows.map(row => etiquetteBody(row, pharmacy)).join("\n")}
</body>
</html>`;
}

export function openEtiquettesPrintWindow(rows, pharmacy, title) {
    if (!rows.length) return;
    const html = generateEtiquettePrintHTML(rows, pharmacy, title);
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = globalThis.open(url, "_blank", "width=420,height=520");
    if (!win) {
        URL.revokeObjectURL(url);
        return;
    }
    win.addEventListener("load", () => {
        URL.revokeObjectURL(url);
        win.focus();
        win.print();
    }, { once: true });
}

const AVATAR_PALETTE = [
    { bg: "#dbeafe", color: "#1d4ed8" },
    { bg: "#dcfce7", color: "#15803d" },
    { bg: "#fef3c7", color: "#b45309" },
    { bg: "#ede9fe", color: "#7c3aed" },
    { bg: "#fce7f3", color: "#be185d" },
    { bg: "#e0f2fe", color: "#0369a1" },
    { bg: "#ffedd5", color: "#c2410c" },
    { bg: "#f0fdf4", color: "#166534" },
];

function hashColor(str) {
    let h = 0;
    for (let i = 0; i < (str || "").length; i++) h = (h * 31 + str.codePointAt(i)) & 0xffff;
    return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function initials(nom, prenom) {
    return (((nom || "")[0] || "") + ((prenom || "")[0] || "")).toUpperCase() || "?";
}

const MED_CLASSIF_STYLE = {
    CNO:                 { bg: "#e3eef6", color: "#1f4f74", label: "CNO" },
    ORTHO_CONTENTIONS:   { bg: "#fbf3dc", color: "#81630e", label: "Ortho/Cont." },
    STUP:                { bg: "#ede9fe", color: "#6d28d9", label: "STUP" },
    MEDICAMENT_EXCEPTION:{ bg: "#fbeae5", color: "#8f3b26", label: "Médicament d'exception" },
    ARRET_TTT:           { bg: "#fbeae5", color: "#8f3b26", label: "ARRET_TTT" },
};
const MED_CLASSIF_DEFAULT_STYLE = { bg: "#eef0ec", color: "#435d57" };

function ClassifPill({ value }) {
    if (!value || value === "UNKNOWN") return null;
    const c = classificationStyle(value);
    return (
        <span className="rbt-classif" style={{ background: c.bg, color: c.color, borderColor: c.border }}>
            {classificationLabel(value)}
        </span>
    );
}

function MedClassifBadge({ value }) {
    if (!value || value === "AUCUNE") return null;
    const c = MED_CLASSIF_STYLE[value] || { ...MED_CLASSIF_DEFAULT_STYLE, label: value };
    return (
        <span className="rbt-med-classif" title={c.label} style={{ background: c.bg, color: c.color }}>
            {c.label}
        </span>
    );
}

/** Case à cocher de validation, suivie de l'horodatage et de l'opérateur une fois cochée. */
export function StampCheck({ checked, at, by, disabled, label, onChange }) {
    return (
        <div className="rbt-check">
            <input
                type="checkbox"
                className="rbt-checkbox"
                aria-label={label}
                checked={!!checked}
                disabled={disabled}
                onChange={onChange}
            />
            {checked && (at || by) && (
                <span className="rbt-stamp">
                    {at && <strong>{formatDateTime(at)}</strong>}
                    {by && <small title={by}>{by}</small>}
                </span>
            )}
        </div>
    );
}

export function FilterField({ label, active = false, children }) {
    return (
        <label className={`rbt-filter${active ? " is-active" : ""}`}>
            <span className="rbt-filter__label">{label}</span>
            {children}
        </label>
    );
}

export function PrinterIcon() {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 9V3h12v6" />
            <rect x="3" y="9" width="18" height="8" rx="2" />
            <path d="M6 14h12v7H6z" />
        </svg>
    );
}

export function RefreshIcon() {
    return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
    );
}

function PencilIcon() {
    return (
        <svg className="rbt-lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
        </svg>
    );
}

/** Cellule « Informations » d'un patient : verrou, identité, accès au mail et classification. */
export function PatientInfo({ row, saveStatus, rowEditedBy, isActiveRow, isLockedByOther, onOpenMail }) {
    const { bg: aBg, color: aColor } = hashColor((row.patientNom || "") + (row.patientPrenom || ""));
    const externalMailLink = row.mailLink || row.gmailLink;

    return (
        <div className="rbt-info">
            {isLockedByOther && <div className="rbt-lock rbt-lock--other">🔒 {rowEditedBy}</div>}
            {isActiveRow && (
                <div className="rbt-lock rbt-lock--self">
                    <PencilIcon />
                    <span>Vous éditez</span>
                </div>
            )}
            <div className="rbt-patient">
                <span className="rbt-avatar" style={{ background: aBg, color: aColor }}>
                    {initials(row.patientNom, row.patientPrenom)}
                </span>
                <div className="rbt-patient__identity">
                    <span className="rbt-patient__name">
                        <span>{row.patientNom ? String(row.patientNom).toLocaleUpperCase("fr") : ""}</span> {row.patientPrenom}
                    </span>
                    <span className="rbt-patient__meta" title={row.ehpadNom || row.ehpadCode || ""}>
                        {row.ehpadCode || row.ehpadNom || "—"} · {formatDate(row.dateReception)}
                    </span>
                </div>
            </div>
            <div className="rbt-chips">
                {onOpenMail && (
                    <button
                        type="button"
                        className="rbt-mail-btn"
                        title="Ouvrir le mail de la commande"
                        onClick={(e) => { e.stopPropagation(); onOpenMail(row.commandeId); }}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="2" y="4" width="20" height="16" rx="2"/>
                            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                        </svg>
                        <span>Voir le mail</span>
                        <span className="rbt-mail-btn__ref">#{row.commandeId}</span>
                    </button>
                )}
                {externalMailLink && (
                    <a
                        className="rbt-chip rbt-chip--mail"
                        href={externalMailLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        title={row.mailProvider === "ORANGE" ? "Voir dans Orange Mail" : "Voir dans la messagerie"}
                    >
                        Messagerie ↗
                    </a>
                )}
                <SaveStatusBadge status={saveStatus} />
            </div>
            <ClassifPill value={row.classification} />
        </div>
    );
}
